import * as React from "react";
import Box from "@mui/material/Box";
import MuiModal from "@mui/material/Modal";
import MuiButton from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import {
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { Input } from "antd";
import { useParams } from "react-router-dom";
import useAxios from "../../utils/useAxios";
import { useToast } from "@chakra-ui/react";

const { Search } = Input;

const MODAL_STYLE = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 480,
  bgcolor: "background.paper",
  boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
  borderRadius: "20px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  maxHeight: "85vh",
};

const TYPE_LABEL = {
  equipment: "Main Asset",
  tier: "Sub-Asset",
  panelComponent: "Panel Component",
};

const TYPE_COLOR = {
  equipment: "primary",
  tier: "secondary",
  panelComponent: "default",
};

const ModalHeader = ({ title, onClose }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: 3,
      py: 2.5,
      borderBottom: "1px solid",
      borderColor: "divider",
      bgcolor: "primary.main",
    }}
  >
    <Typography
      variant="h6"
      sx={{ fontWeight: 700, color: "#fff", letterSpacing: 0.3 }}
    >
      {title}
    </Typography>
    <IconButton
      onClick={onClose}
      size="small"
      sx={{ color: "#fff", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>
);

// ─── Asset Sub-Group ──────────────────────────────────────────────────────────
const AssetSubGroup = ({
  label,
  items,
  checked,
  onToggle,
  searchQuery,
  getLabel,
}) => {
  const filtered = items.filter((item) =>
    getLabel(item).toLowerCase().includes(searchQuery.toLowerCase()),
  );
  if (filtered.length === 0) return null;

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        mb: 1,
        borderColor: "divider",
        borderRadius: "10px !important",
        "&:before": { display: "none" },
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ bgcolor: "grey.50", "& .MuiAccordionSummary-content": { my: 1 } }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.8,
            color: "secondary.main",
          }}
        >
          {label} ({filtered.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, py: 1 }}>
        {filtered.map((item) => (
          <Box
            key={item.id}
            sx={{
              display: "flex",
              alignItems: "center",
              px: 1,
              py: 0.5,
              borderRadius: "8px",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={checked.some(
                    (c) => c.id === item.id && c.type === item._type,
                  )}
                  onChange={() => onToggle(item)}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {getLabel(item)}
                </Typography>
              }
              sx={{ m: 0, width: "100%" }}
            />
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LinkDoc({
  deliverableId,
  externalOpen = false,
  onClose,
  // Explicit scope overrides. AssetDocuments lives under a
  // `/sites/:siteId/...` route, so it never passes these — `siteId` keeps
  // coming from useParams below, same as before. GlobalDocuments has no
  // siteId in its URL at all (it tracks site/plant as filter state instead,
  // via DocumentsFilters), so it passes these in to scope the "Link New
  // Asset" browser the same way the filter bar does.
  //
  // Intentionally site/plant only, not equipment: a document can link to
  // several assets at different levels within a plant (an equipment, one of
  // its tiers, a panel component, etc.), so narrowing the browser down to
  // one specific piece of equipment would get in the way of that.
  siteId: siteIdProp,
  plantId = "",
}) {
  const api = useAxios();
  const toast = useToast();
  const { siteId: siteIdFromRoute } = useParams();
  // "" from an unset filter shouldn't win over a real route param, so `||`
  // rather than `??` here.
  const siteId = siteIdProp || siteIdFromRoute;

  const [parentOpen, setParentOpen] = React.useState(false);
  const [childOpen, setChildOpen] = React.useState(false);
  const [parentSearchQuery, setParentSearchQuery] = React.useState("");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [checked, setChecked] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [delinking, setDelinking] = React.useState(null); // id of link being deleted

  const ASSET_TYPES = ["equipment", "tier", "panelcomponent"];

  // Fetched assets
  const [equipment, setEquipment] = React.useState([]);
  const [tiers, setTiers] = React.useState([]);
  const [panelComponents, setPanelComponents] = React.useState([]);

  // Per-site cache so reopening the child modal for a site we've already
  // loaded is instant instead of re-fetching everything again. Entries
  // expire after ASSETS_STALE_TIME so long-lived instances don't hide
  // newly-created assets indefinitely.
  const assetsCacheRef = React.useRef({});
  const ASSETS_STALE_TIME = 60 * 1000; // 1 minute

  // Existing links for this deliverable
  const [linkedAssets, setLinkedAssets] = React.useState([]);

  // ── Fetch existing links ──────────────────────────────────────────────────
  const fetchLinks = React.useCallback(async () => {
    if (!deliverableId) {
      setLinkedAssets([]);
      return;
    }
    try {
      const res = await api.get(
        `/deliverable-links/?deliverable=${deliverableId}`,
      );
      // ✅ handle both paginated and non-paginated responses
      const data = Array.isArray(res.data)
        ? res.data
        : (res.data.results ?? []);
      // 🔧 Defensive scoping: keep only links that actually belong to THIS
      // deliverable. If the API ever returns links for other documents
      // (unfiltered response, wrong query param, stale cache, etc.), those
      // extra rows were being merged straight into linkedSet — which made
      // assets that are NOT linked to this document look "already linked"
      // and silently disappear from the "Link New Asset" modal.
      const ownLinks = data.filter(
        (link) =>
          String(
            link.deliverable ?? link.deliverable_id ?? link.deliverableId,
          ) === String(deliverableId),
      );
      setLinkedAssets(ownLinks);
    } catch (err) {
      console.error("Failed to fetch links:", err);
    }
  }, [deliverableId]);

  // ✅ open when triggered externally
  React.useEffect(() => {
    if (externalOpen) setParentOpen(true);
  }, [externalOpen]);

  // Add this effect alongside the other useEffects
  React.useEffect(() => {
    if (parentOpen) fetchLinks();
  }, [parentOpen, fetchLinks]);

  const handleParentClose = () => {
    setParentOpen(false);
    setParentSearchQuery("");
    onClose?.(); // notify AssetDocs to clear linkDocCard
  };

  // ── Fetch all assets when child opens ────────────────────────────────────
  React.useEffect(() => {
    if (!childOpen || !siteId) return;

    // ✅ Serve from cache instantly on repeat opens for the same site,
    // as long as it's still within the staleTime window.
    const cached = assetsCacheRef.current[siteId];
    if (cached && Date.now() - cached.fetchedAt < ASSETS_STALE_TIME) {
      setEquipment(cached.equipment);
      setTiers(cached.tiers);
      setPanelComponents(cached.panelComponents);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let firstBatchIn = false;
    const unblock = () => {
      // Clear the spinner as soon as the FIRST category comes back instead
      // of waiting on Promise.all for the slowest of the three — the user
      // can start browsing/searching immediately and the rest merge in as
      // they arrive.
      if (!firstBatchIn && !cancelled) {
        firstBatchIn = true;
        setLoading(false);
      }
    };

    setLoading(true);

    // Follow DRF's `next` link so a paginated response doesn't silently
    // truncate to page one — same pattern used for the other paginated
    // endpoints in Plants.jsx/Equipment.jsx.
    const fetchAllPages = async (url) => {
      const res = await api.get(url);
      if (!res.data || !Array.isArray(res.data.results)) {
        // Non-paginated response — just return it as-is.
        return Array.isArray(res.data) ? res.data : [];
      }
      let results = res.data.results;
      let nextUrl = res.data.next;
      while (nextUrl) {
        const nextRes = await api.get(nextUrl);
        results = results.concat(nextRes.data.results ?? []);
        nextUrl = nextRes.data.next;
      }
      return results;
    };

    const eqPromise = fetchAllPages(`/equipment/?site=${siteId}`)
      .then((data) => {
        const items = data.map((e) => ({ ...e, _type: "equipment" }));
        if (!cancelled) setEquipment(items);
        return items;
      })
      .catch((err) => {
        console.error("Failed to fetch equipment:", err);
        return [];
      })
      .finally(unblock);

    const tierPromise = fetchAllPages(`/tiers/?site=${siteId}`)
      .then((data) => {
        const items = data.map((t) => ({ ...t, _type: "tier" }));
        if (!cancelled) setTiers(items);
        return items;
      })
      .catch((err) => {
        console.error("Failed to fetch tiers:", err);
        return [];
      })
      .finally(unblock);

    const pcPromise = fetchAllPages(`/panel-components/?site=${siteId}`)
      .then((data) => {
        const items = data.map((p) => ({
          ...p,
          _type: "panelComponent",
        }));
        if (!cancelled) setPanelComponents(items);
        return items;
      })
      .catch((err) => {
        console.error("Failed to fetch panel components:", err);
        return [];
      })
      .finally(unblock);

    Promise.all([eqPromise, tierPromise, pcPromise]).then(
      ([eq, tiers, pcs]) => {
        if (cancelled) return;
        assetsCacheRef.current[siteId] = {
          equipment: eq,
          tiers,
          panelComponents: pcs,
          fetchedAt: Date.now(),
        };
        setLoading(false); // in case all three raced in before unblock ran
      },
    );

    return () => {
      cancelled = true;
    };
  }, [childOpen, siteId]);

  const filteredLinks = linkedAssets
    .filter((link) => ASSET_TYPES.includes(link.parent_type.toLowerCase()))
    .filter((link) => {
      const q = parentSearchQuery.toLowerCase();
      return (
        (link.parent_name ?? "").toLowerCase().includes(q) ||
        (link.plant_name ?? "").toLowerCase().includes(q) ||
        String(link.parent_id ?? "").includes(q) // fallback if parent_name is null
      );
    });

  // ── Build a set of already-linked keys for fast lookup ───────────────────
  const linkedSet = React.useMemo(() => {
    const set = new Set();
    linkedAssets
      .filter((link) => ASSET_TYPES.includes(link.parent_type.toLowerCase()))
      .forEach((link) =>
        set.add(`${link.parent_type.toLowerCase()}:${link.parent_id}`),
      );
    return set;
  }, [linkedAssets]);

  // isAlreadyLinked normalises _type too
  const isAlreadyLinked = (item) =>
    linkedSet.has(`${item._type.toLowerCase()}:${item.id}`);

  // ── Group by plant, excluding already-linked ─────────────────────────────
  // Confirmed against models.py: Equipment.plant, Tier.plant, and
  // PanelComponent.plant are all plain FKs to Plant, so plant-scoping below
  // is safe for all three types. Deliberately no equipment-level narrowing —
  // see the comment on the `plantId`/component props above.
  const matchesScope = (item) => {
    if (plantId) {
      const itemPlantId = item.plant ?? item.plant_details?.id ?? null;
      if (String(itemPlantId) !== String(plantId)) return false;
    }
    return true;
  };

  const groupedByPlant = React.useMemo(() => {
    const map = {};
    const addToMap = (items) => {
      items.forEach((item) => {
        if (isAlreadyLinked(item)) return; // filter out already linked
        if (!matchesScope(item)) return;
        const plantKey = item.plant ?? item.plant_details?.id ?? "unknown";
        const plantName = item.plant_details?.siteName ?? "Unassigned";
        if (!map[plantKey])
          map[plantKey] = {
            plantName,
            equipment: [],
            tiers: [],
            panelComponents: [],
          };
        if (item._type === "equipment") map[plantKey].equipment.push(item);
        if (item._type === "tier") map[plantKey].tiers.push(item);
        if (item._type === "panelComponent")
          map[plantKey].panelComponents.push(item);
      });
    };
    addToMap(equipment);
    addToMap(tiers);
    addToMap(panelComponents);
    return map;
  }, [equipment, tiers, panelComponents, linkedSet, plantId]);

  const plantHasResults = (group) => {
    const q = searchQuery.toLowerCase();
    return (
      group.equipment.some((e) =>
        `${e.tagNumber ?? ""} ${e.equipmentName ?? ""}`
          .toLowerCase()
          .includes(q),
      ) ||
      group.tiers.some((t) =>
        `${t.tagNumber ?? ""} ${t.tierName ?? ""}`.toLowerCase().includes(q),
      ) ||
      group.panelComponents.some((p) =>
        `${p.tagNumber ?? ""} ${p.componentName ?? ""}`
          .toLowerCase()
          .includes(q),
      )
    );
  };

  const handleToggle = (item) => {
    setChecked((prev) => {
      const exists = prev.some(
        (c) => c.id === item.id && c.type === item._type,
      );
      return exists
        ? prev.filter((c) => !(c.id === item.id && c.type === item._type))
        : [
            ...prev,
            { id: item.id, type: item._type, label: getItemLabel(item) },
          ];
    });
  };

  const getItemLabel = (item) => {
    if (item._type === "equipment")
      return `${item.tagNumber ?? ""} - ${item.equipmentName ?? ""}`.trim();
    if (item._type === "tier")
      return `${item.tagNumber ?? ""} - ${item.tierName ?? ""}`.trim();
    return `${item.tagNumber ?? ""} - ${item.componentName ?? ""}`.trim();
  };

  // ── Save selected links ───────────────────────────────────────────────────
  const handleSave = async () => {
    if (!checked.length) return;
    setSaving(true);

    const typeMapping = {
      equipment: "equipment",
      tier: "tier",
      panelComponent: "panelcomponent", // ✅ matches ContentType.model
    };

    try {
      const links = checked.map((c) => ({
        deliverable: deliverableId,
        parent_type_input: typeMapping[c.type], // ✅ renamed
        parent_id: c.id,
      }));

      await api.post("/deliverable-links/", links);
      await fetchLinks();

      toast({
        title: "Success",
        description: `${checked.length} asset${checked.length !== 1 ? "s" : ""} linked successfully.`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });

      setChecked([]);
      setChildOpen(false);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to link assets.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Delink an asset ───────────────────────────────────────────────────────
  const handleDelink = async (linkId) => {
    setDelinking(linkId);
    try {
      await api.delete(`/deliverable-links/${linkId}/`);
      setLinkedAssets((prev) => prev.filter((l) => l.id !== linkId));
      toast({
        title: "Unlinked",
        description: "Asset removed from this document.",
        status: "info",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to unlink asset.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setDelinking(null);
    }
  };

  const handleOpenChild = () => setChildOpen(true);
  const handleCloseChild = () => {
    setChildOpen(false);
    setSearchQuery("");
    setChecked([]);
  };

  return (
    <div>
      <Typography
        onClick={() => setParentOpen(true)}
        variant="body1"
        sx={{ cursor: "pointer", color: "primary.main", fontWeight: 600 }}
      >
        Link Document
      </Typography>

      {/* ─── Parent Modal ─── */}
      <MuiModal open={parentOpen} onClose={handleParentClose}>
        <Box sx={MODAL_STYLE}>
          <ModalHeader title="Linked Assets" onClose={handleParentClose} />

          <Box
            sx={{
              px: 3,
              pt: 2.5,
              pb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Assets linked to this document.
            </Typography>
            <MuiButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenChild}
              size="small"
              sx={{
                bgcolor: "primary.main",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "primary.dark" },
              }}
            >
              Add Link
            </MuiButton>
          </Box>

          <Box sx={{ px: 3, py: 2, flexGrow: 1, overflowY: "auto" }}>
            <Box sx={{ mb: 2 }}>
              <Search
                placeholder="Search linked assets..."
                allowClear
                enterButton
                value={parentSearchQuery}
                onChange={(e) => setParentSearchQuery(e.target.value)}
                onSearch={(value) => setParentSearchQuery(value)}
                styles={{
                  input: { borderRadius: "10px 0 0 10px", fontSize: 14 },
                  affixWrapper: { borderRadius: "10px 0 0 10px" },
                }}
              />
            </Box>
            {filteredLinks.length === 0 ? (
              <Box
                sx={{
                  height: 120,
                  borderRadius: "12px",
                  border: "2px dashed",
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body2" color="text.disabled">
                  {parentSearchQuery
                    ? "No matching assets found."
                    : "No linked assets yet"}
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {linkedAssets
                  .filter((link) =>
                    ASSET_TYPES.includes(link.parent_type.toLowerCase()),
                  )
                  .map((link) => (
                    <ListItem
                      key={link.id}
                      disablePadding
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        mb: 0.75,
                        borderRadius: "10px",
                        border: "1px solid",
                        borderColor: "divider",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={600}>
                            {link.parent_name ?? `ID: ${link.parent_id}`}
                          </Typography>
                        }
                        secondary={
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.75,
                              mt: 0.5,
                              flexWrap: "wrap",
                            }}
                          >
                            <Chip
                              label={
                                TYPE_LABEL[link.parent_type] ?? link.parent_type
                              }
                              color={TYPE_COLOR[link.parent_type] ?? "default"}
                              size="small"
                              sx={{ height: 20, fontSize: "0.65rem" }}
                            />
                            {link.plant_name && (
                              <Chip
                                label={link.plant_name}
                                variant="outlined"
                                size="small"
                                sx={{ height: 20, fontSize: "0.65rem" }}
                              />
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          size="small"
                          onClick={() => handleDelink(link.id)}
                          disabled={delinking === link.id}
                          sx={{
                            color: "error.main",
                            "&:hover": { bgcolor: "error.50" },
                          }}
                        >
                          {delinking === link.id ? (
                            <CircularProgress size={16} color="error" />
                          ) : (
                            <LinkOffIcon fontSize="small" />
                          )}
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
              </List>
            )}
          </Box>
        </Box>
      </MuiModal>

      {/* ─── Child Modal ─── */}
      <MuiModal open={childOpen} onClose={handleCloseChild}>
        <Box sx={MODAL_STYLE}>
          <ModalHeader title="Link New Asset" onClose={handleCloseChild} />

          <Box sx={{ px: 3, py: 3, flexGrow: 1, overflowY: "auto" }}>
            <Box sx={{ mb: 2.5 }}>
              <Search
                placeholder="Search assets..."
                allowClear
                enterButton
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onSearch={(value) => setSearchQuery(value)}
                styles={{
                  input: { borderRadius: "10px 0 0 10px", fontSize: 14 },
                  affixWrapper: { borderRadius: "10px 0 0 10px" },
                }}
              />
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : !siteId ? (
              <Typography
                variant="body2"
                color="text.disabled"
                align="center"
                sx={{ py: 4 }}
              >
                Select a site from the filters above to see assets you can link.
              </Typography>
            ) : Object.keys(groupedByPlant).length === 0 ? (
              <Typography
                variant="body2"
                color="text.disabled"
                align="center"
                sx={{ py: 4 }}
              >
                No assets found for this {plantId ? "plant" : "site"}.
              </Typography>
            ) : (
              Object.entries(groupedByPlant)
                .filter(([, group]) => plantHasResults(group))
                .map(([plantId, group]) => (
                  <Accordion
                    key={plantId}
                    disableGutters
                    elevation={0}
                    sx={{
                      mb: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "12px !important",
                      "&:before": { display: "none" },
                      overflow: "hidden",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon sx={{ color: "primary.main" }} />
                      }
                      sx={{
                        bgcolor: "primary.50",
                        "& .MuiAccordionSummary-content": { my: 1 },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, color: "primary.main" }}
                      >
                        {group.plantName}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, py: 1.5 }}>
                      <AssetSubGroup
                        label="Main Assets"
                        items={group.equipment}
                        checked={checked}
                        onToggle={handleToggle}
                        searchQuery={searchQuery}
                        getLabel={(e) =>
                          `${e.tagNumber ?? ""} - ${e.equipmentName ?? ""}`.trim()
                        }
                      />
                      <AssetSubGroup
                        label="Sub-Assets"
                        items={group.tiers}
                        checked={checked}
                        onToggle={handleToggle}
                        searchQuery={searchQuery}
                        getLabel={(t) =>
                          `${t.tagNumber ?? ""} - ${t.tierName ?? ""}`.trim()
                        }
                      />
                      <AssetSubGroup
                        label="Panel Components"
                        items={group.panelComponents}
                        checked={checked}
                        onToggle={handleToggle}
                        searchQuery={searchQuery}
                        getLabel={(p) =>
                          `${p.tagNumber ?? ""} - ${p.componentName ?? ""}`.trim()
                        }
                      />
                    </AccordionDetails>
                  </Accordion>
                ))
            )}
          </Box>

          <Box
            sx={{
              px: 3,
              py: 2,
              borderTop: "1px solid",
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {checked.length} asset{checked.length !== 1 ? "s" : ""} selected
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <MuiButton
                onClick={handleCloseChild}
                variant="outlined"
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Cancel
              </MuiButton>
              <MuiButton
                variant="contained"
                disabled={checked.length === 0 || saving}
                onClick={handleSave}
                sx={{
                  bgcolor: "primary.main",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "primary.dark" },
                  minWidth: 80,
                }}
              >
                {saving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Save"
                )}
              </MuiButton>
            </Box>
          </Box>
        </Box>
      </MuiModal>
    </div>
  );
}
